'use client';

import { ShowcaseIcon } from '../../collection-showcase/scenes/scene-primitives.jsx';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import {
  ShowcaseCopyContext,
  translateShowcaseNode,
} from '../../collection-showcase/translation';
import {
  Ma,
  ImaiStatus,
  ChatBubble,
  TypingIndicator,
  MessageTime,
  ChatTime,
  ImaiAvatar,
  Oa,
} from './approvals-animation';
import { BusinessIcon } from './business-primitives';
import { C, Se } from './business-facts';
export function ThreadPreview({ title: e, status: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={`flex w-full ${Ma.run}`}>
      <span
        className={
          'flex h-10 max-w-[88%] min-w-0 items-center gap-2.5 rounded-[20px] border border-[var(--border)] px-4 text-[15px] leading-5'
        }
      >
        <span className={'swap size-4 shrink-0'}>
          {translateShowcaseNode(
            showcaseCopy,
            t === 'done' ? (
              <BusinessIcon
                key={'done'}
                name={'message'}
                className={'swap-in text-ink-3 size-4'}
              />
            ) : (
              <ImaiStatus
                key={t}
                awaiting={t === 'needs'}
                className={'swap-in size-4'}
              />
            )
          )}
        </span>
        <span className={'text-ink min-w-0 truncate'}>
          {translateShowcaseNode(showcaseCopy, e)}
        </span>
        {translateShowcaseNode(
          showcaseCopy,
          t === 'needs' ? (
            <span
              className={'swap-in text-ink-3 shrink-0 text-[12px] leading-4'}
            >
              {translateShowcaseNode(showcaseCopy, 'Needs you')}
            </span>
          ) : null
        )}
      </span>
    </div>
  );
}
export function ChatFile({ name: e, kind: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={`flex w-full ${Ma.run}`}>
      <span
        className={
          'flex h-14 max-w-[88%] items-center gap-3 rounded-[20px] bg-[var(--muted)] py-2 pr-4 pl-2.5'
        }
      >
        <span
          className={
            'grid size-10 shrink-0 place-items-center rounded-[12px] bg-[var(--background)] text-[oklch(0.55_0.13_155)] shadow-[var(--sh-1)]'
          }
        >
          <ShowcaseIcon name={'sheet'} className={'size-5'} />
        </span>
        <span className={'min-w-0'}>
          <span
            className={
              'text-ink block truncate text-[14px] leading-5 font-medium'
            }
          >
            {translateShowcaseNode(showcaseCopy, e)}
          </span>
          <span className={'text-ink-3 block text-[12px] leading-4'}>
            {translateShowcaseNode(showcaseCopy, t)}
          </span>
        </span>
      </span>
    </div>
  );
}
export const Va = C.closing.base;
export const Ha = C.closing.downside;
export const Ua = 24e4;
export function ChatCashChart() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let e = 232 / Va.length,
    t = (e) => 72 - (e / Ua) * 72,
    n = C.low.week - 1,
    r = Ha.map((n, r) => `${r * e + e / 2},${t(n)}`).join(' L');
  return (
    <div className={`flex w-full ${Ma.run}`}>
      <figure
        className={'m-0 rounded-[20px] bg-[var(--muted)] px-4 pt-3 pb-2.5'}
      >
        <figcaption
          className={
            'text-ink-3 flex items-center justify-between gap-4 text-[12px] leading-4'
          }
        >
          <span>
            {translateShowcaseNode(showcaseCopy, 'Closing cash, 13 weeks')}
          </span>
          <span className={'text-ink-4'}>
            {translateShowcaseNode(showcaseCopy, 'illustrative')}
          </span>
        </figcaption>
        <svg
          viewBox={'0 0 232 74'}
          role={'img'}
          aria-label={translateShowcaseNode(
            showcaseCopy,
            'Weekly closing cash dips to $167,000 in week 9; downside $111,200.'
          )}
          className={'mt-2 block h-[74px] w-[232px] overflow-visible'}
        >
          {translateShowcaseNode(
            showcaseCopy,
            Va.map((r, i) => (
              <rect
                key={i}
                x={i * e + e * 0.18}
                y={t(r)}
                width={e * 0.64}
                height={72 - t(r)}
                rx={1.5}
                fill={i === n ? 'var(--ref-amber)' : 'var(--foreground)'}
                opacity={i === n ? 0.95 : 0.55}
                className={'bz-bar-in'}
                style={{
                  transformOrigin: `${i * e + e / 2}px 72px`,
                  animationDelay: `${i * 45}ms`,
                }}
              />
            ))
          )}
          <path
            d={`M${r}`}
            fill={'none'}
            stroke={'var(--ref-violet)'}
            strokeWidth={1.3}
            strokeDasharray={'3 2.5'}
          />
          <line x1={0} x2={232} y1={72} y2={72} stroke={'var(--border)'} />
        </svg>
        <p
          className={
            'num text-ink-3 mt-1.5 flex justify-between text-[11.5px] leading-4'
          }
        >
          <span>{translateShowcaseNode(showcaseCopy, 'Wk 1')}</span>
          <span className={'text-ink font-medium'}>
            {translateShowcaseNode(showcaseCopy, 'Low wk 9 \xB7 $167k')}
          </span>
          <span>{translateShowcaseNode(showcaseCopy, 'Wk 13')}</span>
        </p>
      </figure>
    </div>
  );
}
export const Ga = 'Mercer & Vale';
export const Ka = `${Ga} forecast our cash for the next 13 weeks`;
export const qa =
  '$180k today. Online ~22k/wk, store ~9k, payroll 28k every 2 weeks, production 45k in wk 3 and wk 9';
export const Ja = `${Ga} 13-week cash forecast`;
export const B = {
  askType: 500,
  askSend: 760 + (Ka.length - 13) * 30 + 250,
  a1Dots: 2600,
  a1: 3400,
  thread: 3700,
  needs: 5200,
  a2Dots: 5400,
  a2: 6400,
  answerType: 7400,
  answerSend: 9806,
  resumed: 10200,
  pause: 11400,
  done: 11700,
  a3Dots: 11800,
  a3: 12800,
  chart: 13300,
  file: 13800,
  sticker: 14400,
};
export const Ya = 15800;
export const Xa = [
  [B.a1Dots, B.a1],
  [B.a2Dots, B.a2],
  [B.a3Dots, B.a3],
];
export function Za(e) {
  return e >= B.done
    ? 'done'
    : e >= B.needs && e < B.resumed
      ? 'needs'
      : 'working';
}
export const Qa = (e, t, n, r, i = !1) => ({
  key: e,
  at: t,
  from: 'imai',
  node: (
    <ChatBubble from={'imai'} gap={n} label={'IMAI'} sticker={i}>
      {r}
    </ChatBubble>
  ),
});
export const $a = (e, t, n, r) => ({
  key: e,
  at: t,
  until: n,
  from: 'imai',
  node: <TypingIndicator gap={r} />,
});
export const eo = C.low;
export const to = (e) => `$${e.toLocaleString('en-US')}`;
export function useChatMessages(e) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = e >= B.answerSend ? 'answer' : 'ask',
    n = (n, r, i) =>
      t === n && e >= r + 250 ? (
        <MessageTime label={e >= i ? 'Read 9:42 AM' : 'Delivered'} />
      ) : null;
  return [
    {
      key: 'time',
      at: 0,
      from: 'meta',
      node: <ChatTime label={'Today 9:41 AM'} gap={'top'} />,
    },
    Qa('g1', 0, 'turn', Se.yourImai.greeting),
    Qa('g2', 120, 'run', Se.yourImai.line),
    {
      key: 'chip',
      at: 240,
      from: 'imai',
      node: (
        <div className={'mt-1 flex'}>
          <span
            className={
              'text-ink inline-flex h-10 items-center gap-2 rounded-[20px] bg-[var(--muted)] ps-3.5 pe-3 text-[15px] leading-5'
            }
          >
            <ImaiAvatar svg={Oa} size={18} />
            {translateShowcaseNode(showcaseCopy, 'Customize your IMAI')}
            <ShowcaseIcon
              name={'chevronRight'}
              className={'text-ink-3 size-4'}
            />
          </span>
        </div>
      ),
    },
    {
      key: 'ask',
      at: B.askSend,
      from: 'user',
      node: (
        <React.Fragment>
          <ChatBubble from={'user'} gap={'turn'} label={'You'}>
            <span className={'font-medium'}>
              {translateShowcaseNode(showcaseCopy, Ga)}
            </span>
            {translateShowcaseNode(showcaseCopy, Ka.slice(13))}
          </ChatBubble>
          {translateShowcaseNode(showcaseCopy, n('ask', B.askSend, B.a1Dots))}
        </React.Fragment>
      ),
    },
    $a('d1', B.a1Dots, B.a1, 'turn'),
    Qa('a1', B.a1, 'turn', `On it. I started ${Ja}.`),
    {
      key: 'card',
      at: B.thread,
      from: 'imai',
      node: (
        <ThreadPreview
          title={translateShowcaseNode(showcaseCopy, Ja)}
          status={Za(e)}
        />
      ),
    },
    $a('d2', B.a2Dots, B.a2, 'run'),
    Qa(
      'a2',
      B.a2,
      'run',
      `${C.needsYou} What’s in the bank today, and roughly what comes in and goes out each week?`
    ),
    {
      key: 'answer',
      at: B.answerSend,
      from: 'user',
      node: (
        <React.Fragment>
          <ChatBubble from={'user'} gap={'turn'} label={'You'}>
            {translateShowcaseNode(showcaseCopy, qa)}
          </ChatBubble>
          {translateShowcaseNode(
            showcaseCopy,
            n('answer', B.answerSend, B.resumed)
          )}
        </React.Fragment>
      ),
    },
    {
      key: 'pause',
      at: B.pause,
      from: 'meta',
      node: <ChatTime label={'9:58 AM'} gap={'pause'} />,
    },
    $a('d3', B.a3Dots, B.a3, 'run'),
    Qa(
      'a3',
      B.a3,
      'run',
      `Done. Lowest week is week ${eo.week} at ${to(eo.base)}, when the second production payment lands. Downside: ${to(eo.downside)}.`,
      e >= B.sticker
    ),
    {
      key: 'chart',
      at: B.chart,
      from: 'imai',
      node: <ChatCashChart />,
    },
    {
      key: 'file',
      at: B.file,
      from: 'imai',
      node: (
        <ChatFile
          name={'Mercer-Vale-13-week-cash.sheet'}
          kind={'Sheet \xB7 13 weeks'}
        />
      ),
    },
  ];
}
export const ro =
  'flex h-9 items-center gap-2 rounded-md px-2.5 text-[14px] font-[360]';
